/**
 * @author    Nikko Renolds
 * @package		Trap Tycoon
 * @link		  https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license		MIT
 * @description Enhanced HUD with dynamic level-based coloring and mobile-first responsive design.
 */

import React, { useState, useEffect } from "react";
import {
  Timer,
  Bell,
  BellRing,
  X,
  Trash2,
  Flame,
  Wallet,
  TrendingUp,
} from "lucide-react";
import { useNotify } from "./Notification";
import StreetPromoterUI from "./StreetPromoterUI";
import { callBackend } from "../utils/api";

export default function StatusBar({ stats, onClaimDaily, onTimerComplete }) {
  const [showPromoterModal, setShowPromoterModal] = useState(false);
  const { notifications, clearNotifications, markAllRead } = useNotify();
  const [showCenter, setShowCenter] = useState(false);
  const xpProgress = stats.xp_progress || 0;
  const [timeLeft, setTimeLeft] = useState("");
  const [maxLevel, setMaxLevel] = useState(50);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const heatLevel = stats.heat_level || 0;
  const heatIcons = Math.min(5, Math.ceil(heatLevel / 20));

  // Dynamic Color Logic based on user requirements
  const getProgressColor = () => {
    const lvl = stats.level;
    // Ready for Prestige Check (Level 150 and 100% XP)
    if (lvl >= maxLevel && xpProgress >= 100) return "#6a029b";

    if (lvl < 10) return "#00a0a8";
    if (lvl < 20) return "#684012";
    if (lvl < 40) return "#645a07";
    if (lvl < 80) return "#526e05";
    if (lvl < 120) return "#750f31";
    return "#38d1a0";
  };

  const isPrestigeReady = stats.level >= maxLevel && xpProgress >= 100;
  const toggleCenter = () => {
    if (!showCenter) markAllRead();
    setShowCenter(!showCenter);
  };

  useEffect(() => {
    // Fetch constants only ONCE on mount
    const fetchConstants = async () => {
      try {
        const data = await callBackend("/system/constants");
        if (data?.ENGINE?.MAX_LEVEL) setMaxLevel(data.ENGINE.MAX_LEVEL);
      } catch (e) {
        console.error(e);
      }
    };
    fetchConstants();
  }, []);

  useEffect(() => {
    if (!stats.nextTickAt) return;

    const timer = setInterval(() => {
      const now = new Date();
      const nextTick = new Date(stats.nextTickAt);
      const diff = nextTick - now;

      if (diff <= 0) {
        setTimeLeft("Processing last tick...");
      } else {
        const mins = Math.floor((diff / 1000 / 60) % 60);
        const secs = Math.floor((diff / 1000) % 60);
        setTimeLeft(`${mins}:${secs < 10 ? "0" : ""}${secs}`);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [stats.nextTickAt]);

  return (
    <div className="w-full bg-[#050505] border-b-2 border-zinc-800 p-2 md:p-4 font-mono relative select-none">
      {/* Top Row: Info & Stats */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        {/* Left Side: Level & Heat */}
        <div className="flex items-center justify-between w-full md:w-auto gap-4 border-b md:border-0 border-zinc-900 pb-2 md:pb-0">
          <div className="flex flex-col">
            <span className="text-[10px] text-zinc-500 font-black uppercase">
              Operation Level
            </span>
            <div className="text-2xl font-black text-white italic tracking-tighter leading-none">
              RANK {stats.level}
            </div>
            {(!!stats.is_advertiser || !!stats.is_booster) && (
              <button
                onClick={() => setShowPromoterModal(true)}
                className="flex items-center gap-1 bg-blue-600/20 border border-blue-500 px-2 py-0.5 text-[9px] font-black text-blue-400 animate-pulse ml-2 hover:bg-blue-600 hover:text-white transition-all"
              >
                <TrendingUp size={10} /> STREET PROMOTER
              </button>
            )}
          </div>

          <div className="h-8 w-[1px] bg-zinc-800 hidden md:block"></div>

          <div className="flex flex-col">
            <span className="text-[10px] text-zinc-500 font-black uppercase">
              Street Heat
            </span>
            <div className="flex items-center gap-1 bg-black px-2 py-1 border border-zinc-800 rounded-sm">
              {[...Array(5)].map((_, i) => (
                <Flame
                  key={i}
                  size={14}
                  className={`${
                    i < heatIcons
                      ? "text-orange-600 fill-orange-600 animate-pulse"
                      : "text-zinc-800"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Center/Right Side: Money & Turns */}
        <div className="flex items-center justify-between w-full md:w-auto gap-6">
          {/* Daily Bonus Button (Responsive) */}
          {stats.canClaimDaily && (
            <button
              onClick={onClaimDaily}
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 z-50 md:relative md:bottom-0 md:left-0 md:translate-x-0 bg-green-600 text-black px-4 py-1 text-[10px] font-black uppercase skew-x-[-12deg] hover:bg-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
            >
              Claim Daily
            </button>
          )}

          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-[10px] text-zinc-500 font-black uppercase block">
                Laundered Cash
              </span>
              <div className="text-xl font-bold text-green-500 flex items-center gap-2 justify-end">
                <Wallet size={16} className="text-green-800" />$
                {stats.cash.toLocaleString()}
              </div>
            </div>

            <div className="h-8 w-[1px] bg-zinc-800"></div>

            <div className="text-right">
              <span className="text-[10px] text-zinc-500 font-black uppercase block leading-none mb-1">
                Turns Left
              </span>
              <div className="text-xl font-bold text-yellow-500 italic leading-none">
                {stats.turns}
              </div>
            </div>
          </div>

          {/* Comms/Notifications */}
          <div className="relative">
            <button
              onClick={toggleCenter}
              className={`p-2 border-2 transition-all ${
                unreadCount > 0
                  ? "border-green-500 text-green-500 animate-pulse shadow-[0_0_15px_rgba(34,197,94,0.2)]"
                  : "border-zinc-800 text-zinc-600 hover:border-zinc-500 hover:text-white"
              }`}
            >
              {unreadCount > 0 ? <BellRing size={20} /> : <Bell size={20} />}
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] px-1.5 py-0.5 font-black border-2 border-black">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar & Timer Row */}
      <div className="mt-4 relative pt-2">
        {/* XP Bar Background */}
        <div className="w-full h-2 bg-zinc-900 border border-zinc-800 p-[1px] rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-700 ease-out rounded-full ${
              isPrestigeReady ? "animate-prestige-flash" : ""
            }`}
            style={{
              width: `${xpProgress}%`,
              backgroundColor: getProgressColor(),
              boxShadow: `0 0 10px ${getProgressColor()}44`,
            }}
          ></div>
        </div>

        {/* Labels below bar */}
        <div className="flex justify-between items-center mt-1">
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-zinc-500 font-bold uppercase">
              EXP Progress
            </span>
            <span className="text-[9px] text-white font-black">
              {stats.experience} XP
            </span>
          </div>

          <div className="flex items-center gap-2 bg-zinc-900/50 px-2 py-0.5 border border-zinc-800 rounded">
            <Timer size={10} className="text-zinc-500" />
            <span className="text-[9px] text-zinc-500 uppercase font-bold">
              Cycle Tick:
            </span>
            <span className="text-[9px] text-white font-black min-w-[35px]">
              {timeLeft}
            </span>
          </div>
        </div>
      </div>

      {/* Notification Center Dropdown */}
      {showCenter && (
        <div className="fixed md:absolute top-20 right-4 left-4 md:left-auto md:w-96 bg-zinc-950 border-2 border-zinc-800 z-[6000] shadow-[20px_20px_0px_0px_rgba(0,0,0,1)]">
          <div className="bg-zinc-900 p-3 border-b border-zinc-800 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-green-500" />
              <span className="text-[11px] text-white font-black uppercase italic tracking-widest">
                Intelligence Feed
              </span>
            </div>
            <div className="flex gap-4">
              <button onClick={clearNotifications} title="Purge Logs">
                <Trash2
                  size={14}
                  className="text-zinc-600 hover:text-red-500 transition-colors"
                />
              </button>
              <button onClick={() => setShowCenter(false)}>
                <X size={14} className="text-zinc-600 hover:text-white" />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto custom-scrollbar">
            {notifications.length === 0 ? (
              <div className="p-12 text-[10px] text-zinc-700 text-center uppercase italic font-bold tracking-widest">
                Static noise... No active transmissions.
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`p-4 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/30 transition-colors relative group ${
                    n.type === "error"
                      ? "bg-red-950/5"
                      : n.type === "success"
                        ? "bg-green-950/5"
                        : ""
                  }`}
                >
                  {/* Lore-based Type Label */}
                  <span
                    className={`text-[8px] font-black px-1 mb-1 inline-block border ${
                      n.type === "error"
                        ? "border-red-600 text-red-600"
                        : n.type === "success"
                          ? "border-green-600 text-green-600"
                          : "border-blue-600 text-blue-600"
                    }`}
                  >
                    {n.type === "error"
                      ? "THE FEDS"
                      : n.type === "success"
                        ? "CLOCKED"
                        : "INTEL"}
                  </span>

                  <p
                    className={`text-[11px] font-bold uppercase leading-tight ${
                      n.type === "error"
                        ? "text-red-500"
                        : n.type === "success"
                          ? "text-green-500"
                          : "text-blue-400"
                    }`}
                  >
                    {n.message}
                  </p>

                  <p className="text-[9px] text-zinc-600 mt-2 font-black italic">
                    PAGER TIME:{" "}
                    {n.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {showPromoterModal && (
        <StreetPromoterUI
          stats={stats}
          onClose={() => setShowPromoterModal(false)}
        />
      )}

      {/* CSS for the Prestige Flashing Effect */}
      <style>{`
        @keyframes prestige-flash {
          0% {
            opacity: 1;
            filter: brightness(1);
          }
          50% {
            opacity: 0.6;
            filter: brightness(1.5);
          }
          100% {
            opacity: 1;
            filter: brightness(1);
          }
        }
        .animate-prestige-flash {
          animation: prestige-flash 1s infinite;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #000;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #27272a;
        }
      `}</style>
    </div>
  );
}
